import { ActivityItem } from 'app/data/types/activity';

/**
 * Whether self check-in is open for this booking right now.
 *
 * `selfCheckInFrom` already encodes the whole policy — service-booking resolves it from the event's
 * self-check-in setting, its advance period, and this booking's own session start — so there is no
 * date arithmetic to repeat here. Absent means attendees may not check themselves in at all
 * (host-scan-only events, and every document projected before the field existed).
 *
 * The end boundary is not redundant with the Upcoming pane: a past booking that was never checked
 * into still carries `registered`, so it lands in Completed with a window that opened long ago.
 * Without this it would offer a check-in for an event that finished last month.
 */
export const isCheckInOpen = (item: ActivityItem, now: number = Date.now()): boolean => {
  if (item.statusKey !== 'registered' || !item.selfCheckInFrom) {
    return false;
  }

  const opensAt = new Date(item.selfCheckInFrom).getTime();
  if (Number.isNaN(opensAt) || now < opensAt) {
    return false;
  }

  // `endTimestamp` is only set when the occurrence has a distinct end; a point-in-time event closes
  // at its start. An unparseable end fails CLOSED, matching the window check above — treating it as
  // "no end" would offer check-in forever on a malformed record.
  const endsAt = new Date(item.endTimestamp ?? item.timestamp).getTime();

  return !Number.isNaN(endsAt) && now <= endsAt;
};

/** helix `CLOSED_CHECK_IN_STATUSES` — a check-in in one of these has been used up or has lapsed. */
const CLOSED_CHECK_IN_STATUSES = new Set(['confirmed', 'canceled', 'auto-canceled']);

/**
 * The only resource-booking status a check-in may be offered from.
 *
 * Narrower than the cancel gate's `CANCELLABLE_BOOKING_STATUS_KEYS` on purpose: a pending request can
 * be withdrawn but not checked into. `checked_in` is absent because it is unreachable here — its
 * check-in row is `confirmed`, which {@link CLOSED_CHECK_IN_STATUSES} already rejects.
 */
const CHECKINABLE_BOOKING_STATUS_KEYS = new Set(['confirmed']);

/**
 * Whether check-in is open for a resource booking right now.
 *
 * Separate from {@link isCheckInOpen} because the two verticals signal it differently. An event
 * publishes only `selfCheckInFrom` and runs to the session's end; a booking has a `check_in` row with
 * its own status and its own expiry, which is NOT the booking's end — helix closes check-in a fixed
 * period after the start.
 *
 * `selfCheckInStatus` is the configured signal, not the timestamp: helix leaves the open time unset when
 * check-in may be done immediately, so gating on `selfCheckInFrom` would offer nothing on exactly
 * those bookings. An absent status means the resource has no check-in at all.
 */
export const isBookingCheckInOpen = (item: ActivityItem, now: number = Date.now()): boolean => {
  if (item.type !== 'resource_booking' || !item.selfCheckInStatus) {
    return false;
  }

  // The BOOKING's own status, not just the check-in row's: helix opens the `check_in` record at
  // creation and closes it on neither approval nor cancellation, so the row says nothing about
  // whether the reservation is real. An allowlist rather than a cancelled-check because the statuses
  // that must NOT offer check-in outnumber the one that must -- `pending_approval` (never approved),
  // `pending_payment` and any unmapped status included, not just the cancelled family.
  if (!CHECKINABLE_BOOKING_STATUS_KEYS.has(item.statusKey)) {
    return false;
  }

  if (CLOSED_CHECK_IN_STATUSES.has(item.selfCheckInStatus.toLowerCase())) {
    return false;
  }

  // Absent open time means "immediately"; an unparseable one fails closed rather than guessing.
  if (item.selfCheckInFrom) {
    const opensAt = new Date(item.selfCheckInFrom).getTime();
    if (Number.isNaN(opensAt) || now < opensAt) {
      return false;
    }
  }

  // Absent expiry means helix cleared it, which it does when the record closes — and a closed record
  // was already caught above, so treat it as no longer offerable rather than open forever.
  const closesAt = new Date(item.selfCheckInUntil ?? '').getTime();
  if (Number.isNaN(closesAt) || now > closesAt) {
    return false;
  }

  // The booking's own end closes it too, and helix's expiry does NOT imply it: helix expires a
  // check-in a fixed period after the START, so on a booking shorter than that period the expiry
  // lands after the booking is over. A quarter-hour room then offered "Check in" next to "Book
  // again" on a slot that had already finished.
  //
  // `endTimestamp` is only set when the occurrence has a distinct end; a point-in-time booking closes
  // at its start. An unparseable end fails closed, matching every other boundary here.
  const endsAt = new Date(item.endTimestamp ?? item.timestamp).getTime();

  return !Number.isNaN(endsAt) && now <= endsAt;
};
